import Peer from 'peerjs';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { VIDEO_STREAM_CONSTRAINS } from '../constants/settings';
import useWebSocket from '../hooks/useWebSocket';
import {
  MutedStatus,
  ParticipantData,
  SessionPeerIDs,
  SessionSocketData,
} from '../types/session';
import {
  comparePeerIds,
  stopStreamTracksHandler,
} from '../utils/commonFunctions';
import { useAuthContext } from './AuthProvider';

interface ISessionContext {
  webSocket: WebSocket | null;
  socketData: SessionSocketData;
  videoDevices: Array<MediaDeviceInfo>;
  setSelectedCamera: React.Dispatch<
    React.SetStateAction<MediaDeviceInfo | null>
  >;
  audioInputDevices: Array<MediaDeviceInfo>;
  setSelectedMic: React.Dispatch<React.SetStateAction<MediaDeviceInfo | null>>;
  cameraPermissions: PermissionStatus | null;
  micPermissions: PermissionStatus | null;
  peerInstance: Peer | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  localScreenStream: MediaStream | null;
  remoteScreenStream: MediaStream | null;
  joinedClient: ParticipantData | undefined;
  mutedStatus: MutedStatus;
  isCameraLoading: boolean;
  isCameraEnabled: boolean;
  isMicEnabled: boolean;
  showFeedbackModal: boolean;
  setShowFeedbackModal: React.Dispatch<React.SetStateAction<boolean>>;
  localStreamRef: React.RefObject<HTMLVideoElement>;
  remoteStreamRef: React.RefObject<HTMLVideoElement>;
  localScreenStreamRef: React.RefObject<HTMLVideoElement>;
  remoteScreenStreamRef: React.RefObject<HTMLVideoElement>;
  askCameraPermissionsHandler: () => void;
  askMicPermissionsHandler: () => void;
  initiateCallHandler: () => void;
  toggleUserCameraHandler: () => void;
  toggleUserMicHandler: () => void;
  toggleScreenShareHandler: () => void;
  callHangUpHandler: () => void;
}

// creating the session context here
const SessionContext = createContext<ISessionContext | null>(null);

const SessionProvider = ({ children }: { children: React.ReactNode }) => {
  // integration of react hooks here
  const [videoDevices, setVideoDevices] = useState<Array<MediaDeviceInfo>>([]);
  const [selectedCamera, setSelectedCamera] = useState<MediaDeviceInfo | null>(
    null
  );

  const [audioInputDevices, setAudioInputDevices] = useState<
    Array<MediaDeviceInfo>
  >([]);
  const [selectedMic, setSelectedMic] = useState<MediaDeviceInfo | null>(null);

  const [cameraPermissions, setCameraPermissions] =
    useState<PermissionStatus | null>(null);
  const [micPermissions, setMicPermissions] = useState<PermissionStatus | null>(
    null
  );

  const [localVideoStream, setLocalVideoStream] = useState<MediaStream | null>(
    null
  );
  const [localAudioStream, setLocalAudioStream] = useState<MediaStream | null>(
    null
  );

  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [localScreenStream, setLocalScreenStream] =
    useState<MediaStream | null>(null);

  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [remoteScreenStream, setRemoteScreenStream] =
    useState<MediaStream | null>(null);

  const [localPeerId, setLocalPeerId] = useState<string>('');
  const [peerIds, setPeerIds] = useState<SessionPeerIDs>({
    associate_peer_id: '',
    client_peer_id: '',
  });

  const [joinedClient, setJoinedClient] = useState<ParticipantData>();

  const [mutedStatus, setMutedStatus] = useState<MutedStatus>({
    is_participant_allow: 'pending',
    is_associate_audio_muted: false,
    is_associate_video_muted: false,
    is_associate_screen_shared: false,
    is_client_audio_muted: false,
    is_client_video_muted: false,
    is_client_screen_shared: false,
  });

  const [peerInstance, setPeerInstance] = useState<Peer | null>(null);

  const [isCameraLoading, setIsCameraLoading] = useState<boolean>(false);
  const [isCameraEnabled, setIsCameraEnabled] = useState<boolean>(true);
  const [isMicEnabled, setIsMicEnabled] = useState<boolean>(true);
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false);

  const localStreamRef = useRef<HTMLVideoElement>(null);
  const localScreenStreamRef = useRef<HTMLVideoElement>(null);

  const remoteStreamRef = useRef<HTMLVideoElement>(null);
  const remoteScreenStreamRef = useRef<HTMLVideoElement>(null);

  // integration of react-router-dom hooks here
  const { pathname } = useLocation();

  // integration of custom hooks here
  const { webSocket, socketData } = useWebSocket(
    `${process.env.REACT_APP_SOCKET_PROD_API}/meeting/${pathname
      .split('/')
      .pop()}/`
  );

  // integration of context hooks here
  const { currentUser, currentUserType } = useAuthContext();

  // session leaving sound audio declared here
  const leavedSessionAudio = new Audio('/assets/audio/session-leave.wav');

  // callback function to ask for microphone access declared here
  const askMicPermissionsHandler = useCallback(() => {
    navigator.mediaDevices
      .getUserMedia({
        audio: {
          deviceId: selectedMic?.deviceId,
        },
      })
      .then((stream) => {
        setLocalAudioStream(stream);
      })
      .catch((error) => {});
  }, [selectedMic]);

  // callback function to ask for camera access declared here
  const askCameraPermissionsHandler = useCallback(() => {
    navigator.mediaDevices
      .getUserMedia({
        video: {
          ...VIDEO_STREAM_CONSTRAINS.video,
          deviceId: selectedCamera?.deviceId,
        },
      })
      .then((stream) => {
        setLocalVideoStream(stream);
      })
      .catch((error) => {});
  }, [selectedCamera]);

  // callback function to get active video devices list declared here
  const getVideoDevicesHandler = useCallback(() => {
    setIsCameraLoading(true);

    navigator.mediaDevices.enumerateDevices().then((devices) => {
      setVideoDevices(devices.filter((device) => device.kind === 'videoinput'));

      console.log('DEVICES: ', devices);

      setSelectedCamera(
        devices.find(
          (device) => device.kind === 'videoinput'
        ) as MediaDeviceInfo
      );

      setIsCameraLoading(false);
    });
  }, []);

  // callback function to get active audio devices list declared here
  const getAudioDevicesHandler = useCallback(() => {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      setAudioInputDevices(
        devices.filter((device) => device.kind === 'audioinput')
      );

      setSelectedMic(
        devices.find(
          (device) => device.kind === 'audioinput'
        ) as MediaDeviceInfo
      );
    });
  }, []);

  // storing user's camera & microphone permissions in local state here
  useMemo(() => {
    navigator.permissions
      .query({ name: 'camera' as PermissionName })
      .then((permissions) => {
        console.log('PERMISSIONS: ', permissions);
        setCameraPermissions(permissions);
      });

    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((permissions) => {
        console.log('PERMISSIONS: ', permissions);
        setMicPermissions(permissions);
      });
  }, []);

  // setting camera or microphone availability based on user's permission here
  useMemo(() => {
    if (cameraPermissions?.state === 'denied') {
      setIsCameraEnabled(false);
    } else if (cameraPermissions?.state === 'granted') {
      getVideoDevicesHandler();
    }

    if (micPermissions?.state === 'denied') {
      setIsMicEnabled(false);
    } else if (micPermissions?.state === 'granted') {
      getAudioDevicesHandler();
    }
  }, [
    cameraPermissions?.state,
    micPermissions?.state,
    getVideoDevicesHandler,
    getAudioDevicesHandler,
  ]);

  // fetching which device the user have (audio and video) here
  useMemo(() => {
    getVideoDevicesHandler();
    getAudioDevicesHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // getting user permission for audio/video devices here
  useMemo(() => {
    askCameraPermissionsHandler();
    askMicPermissionsHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  console.log('LOCALS: ', localAudioStream, localVideoStream);

  // setting user's local media into state after permission here
  useEffect(() => {
    if (!localAudioStream && !localVideoStream) {
      return;
    }

    const stream = new MediaStream();

    console.log('LOCAL STREAM PROVIDER CALLING!!');

    localAudioStream && stream.addTrack(localAudioStream.getAudioTracks()[0]);
    localVideoStream && stream.addTrack(localVideoStream.getVideoTracks()[0]);

    setLocalStream(stream);

    // if (currentUserType === 'client') {
    //   if (mutedStatus.is_participant_allow === 'confirmed') {
    //     navigator.mediaDevices
    //       .getUserMedia(STREAM_CONSTRAINS)
    //       .then((stream) => setLocalStream(stream))
    //       .catch((error) =>
    //         toast.warn(error.message, { toastId: 'local-video-stream' })
    //       );
    //   }
    // } else {

    // }

    return () => {
      stream.getTracks().forEach((track) => {
        track.stop();
        console.log('TRACKS REMOVED!!');
        stream.removeTrack(track);
      });

      setLocalStream(null);
    };
  }, [localAudioStream, localVideoStream]);

  // sending local peer ID through websocket here
  useEffect(() => {
    if (webSocket) {
      webSocket.send(
        JSON.stringify(
          currentUserType === 'user'
            ? {
                associate_id: currentUser?.id,
                associate_peer_id: localPeerId,
                is_associate_audio_muted: mutedStatus.is_associate_audio_muted,
                is_associate_video_muted: mutedStatus.is_associate_video_muted,
                is_associate_screen_shared:
                  mutedStatus.is_associate_screen_shared,
              }
            : {
                client_id: currentUser?.id,
                client_peer_id: localPeerId,
                is_client_audio_muted: mutedStatus.is_client_audio_muted,
                is_client_video_muted: mutedStatus.is_client_video_muted,
                is_client_screen_shared: mutedStatus.is_client_screen_shared,
              }
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocket, localPeerId, currentUser, currentUserType]);

  // parsing peer IDs & participant list from websocket here
  useEffect(() => {
    try {
      const parsedSocketData = JSON.parse(
        (socketData as SessionSocketData).session
      ).data as Array<any>;

      const participantList = JSON.parse(
        (socketData as SessionSocketData).participant_session
      ) as any;

      console.log(
        'SOCKET DATA2: ',
        JSON.parse((socketData as SessionSocketData).session).data,
        participantList
      );

      setJoinedClient(participantList);

      if (
        !comparePeerIds(peerIds, parsedSocketData[parsedSocketData.length - 1])
      ) {
        setPeerIds(parsedSocketData[parsedSocketData.length - 1]);
      }

      console.log(
        'SOCKET STATUS: ',
        (socketData as SessionSocketData).muted_status
      );
      setMutedStatus((socketData as SessionSocketData).muted_status);
    } catch (error) {
      console.log('SOCKET DATA ERROR: ', socketData);
      if (
        socketData &&
        ((socketData as any).user === 'associate' ||
          (socketData as any).user === 'client')
      ) {
        if ((socketData as any).user === 'associate') {
          webSocket?.send(JSON.stringify({ is_participant_allow: 'pending' }));
        }

        // playing user leaving audio sound here
        leavedSessionAudio.play();

        setRemoteStream(null);
        setRemoteScreenStream(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketData]);

  // setting remote stream default video muted state here
  useEffect(() => {
    if (localStream) {
      const videoTrack = localStream
        .getTracks()
        .find((track) => track.kind === 'video');

      if (videoTrack) {
        videoTrack.enabled = isCameraEnabled;
      }
    }
  }, [isCameraEnabled, localStream]);

  // setting remote stream default audio muted state here
  useEffect(() => {
    if (localStream) {
      const audioTrack = localStream
        .getTracks()
        .find((track) => track.kind === 'audio');

      if (audioTrack) {
        audioTrack.enabled = isMicEnabled;
      }
    }
  }, [isMicEnabled, localStream]);

  // replacing audio track on changing microphone here
  const replaceMICTrack = useCallback(
    (deviceId: string) => {
      navigator.mediaDevices
        .getUserMedia({
          audio: {
            deviceId: { exact: deviceId },
          },
        })
        .then((stream) => {
          const newTrack = stream.getAudioTracks()[0];

          if (!newTrack || !localStream) return;

          const oldTrack = localStream.getAudioTracks()[0];

          if (oldTrack) {
            localStream.removeTrack(oldTrack);
            oldTrack.stop();
          }

          localStream.addTrack(newTrack);
        });
    },
    [localStream]
  );

  useEffect(() => {
    if (selectedMic?.deviceId) {
      replaceMICTrack(selectedMic.deviceId);
    }
  }, [selectedMic, replaceMICTrack]);

  // removing remote shared screen when screen is closed
  useEffect(() => {
    if (!mutedStatus.is_associate_screen_shared) {
      if (currentUserType === 'client') {
        setRemoteScreenStream(null);
      }
    }

    if (!mutedStatus.is_client_screen_shared) {
      if (currentUserType === 'user') {
        setRemoteScreenStream(null);
      }
    }
  }, [mutedStatus, currentUserType]);

  // initiating the peer js connection with event listeners here
  // useEffect(() => {
  //   if (localStream) {
  //     const peer = new Peer();

  //     peer.on('open', (id) => {
  //       console.log('OPENING PEER!!');
  //       console.log('PEER ID: ', id);

  //       setLocalPeerId(id);
  //     });

  //     peer.on('call', (call) => {
  //       if (currentUserType === 'client') {
  //         const videoTrack = localStream
  //           ?.getVideoTracks()
  //           .find((track) => track.kind === 'video');

  //         const audioTrack = localStream
  //           ?.getAudioTracks()
  //           .find((track) => track.kind === 'audio');

  //         console.log('WMS: ', mutedStatus);

  //         webSocket?.send(
  //           JSON.stringify({
  //             client_id: joinedClient?.participant_id,
  //             is_client_screen_shared: true,
  //             is_associate_screen_shared: true,
  //             is_client_video_muted: !videoTrack?.enabled,
  //             is_client_audio_muted: !audioTrack?.enabled,
  //           })
  //         );
  //       }

  //       call.answer(localStream);

  //       console.log('[PEER] ANSWERING CALL!!', call);

  //       call.on('stream', (remoteStream) => {
  //         console.log('REMOTE STREAM: ', remoteStream);

  //         if (
  //           remoteStream.getVideoTracks().length > 0 &&
  //           remoteStream.getAudioTracks().length > 0
  //         ) {
  //           console.log('AUDIO/VIDEO');
  //           setRemoteStream(remoteStream);
  //         } else if (
  //           remoteStream.getVideoTracks().length > 0 &&
  //           remoteStream.getAudioTracks().length === 0
  //         ) {
  //           console.log('SCREEN SHARE');
  //           setRemoteScreenStream(remoteStream);
  //         } else if (remoteStream.getAudioTracks().length > 0) {
  //           console.log('AUDIO ONLY');
  //           setRemoteStream(remoteStream);
  //         }
  //       });

  //       call.on('close', () => {
  //         console.log('CLOSING CALL!!');

  //         setRemoteStream(null);
  //         setRemoteScreenStream(null);
  //       });
  //     });

  //     peer.on('close', () => {
  //       console.log('CLOSING PEER!!');

  //       setRemoteStream(null);
  //       setRemoteScreenStream(null);
  //     });

  //     setPeerInstance(peer);

  //     return () => {
  //       peer.disconnect();
  //       peer.destroy();
  //     };
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [localStream]);

  // initiating the call from here
  useEffect(() => {
    // mutedStatus.is_participant_allow === 'confirmed'
    if (peerInstance && localStream) {
      console.log('PEER IDS: ', peerIds);
      console.log('CALLING!!');
      peerInstance.call(
        currentUserType === 'user'
          ? peerIds.client_peer_id
          : peerIds.associate_peer_id,
        localStream
      );
    }
  }, [peerInstance, localStream, peerIds, currentUserType]);

  // initiating the call from here
  useEffect(() => {
    // mutedStatus.is_participant_allow === 'confirmed'
    if (peerInstance && localScreenStream) {
      console.log('CALLING SCREEN SHARE!!');
      peerInstance.call(
        currentUserType === 'user'
          ? peerIds.client_peer_id
          : peerIds.associate_peer_id,
        localScreenStream
      );
    }
  }, [peerInstance, localScreenStream, peerIds, currentUserType]);

  // handler function to initiate call declared here
  const initiateCallHandler = () => {
    if (localStream) {
      const peer = new Peer();

      console.log('INITIATING PEER');

      peer.on('open', (id) => {
        console.log('OPENING PEER!!');
        console.log('PEER ID: ', id);

        setLocalPeerId(id);
      });

      peer.on('call', (call) => {
        const videoTrack = localStream
          ?.getVideoTracks()
          .find((track) => track.kind === 'video');

        const audioTrack = localStream
          ?.getAudioTracks()
          .find((track) => track.kind === 'audio');

        if (currentUserType === 'client') {
          console.log('WMS: ', mutedStatus);

          webSocket?.send(
            JSON.stringify({
              client_id: joinedClient?.participant_id,
              is_client_video_muted: !videoTrack?.enabled,
              is_client_audio_muted: !audioTrack?.enabled,
            })
          );
        }

        call.answer(localStream);

        console.log('[PEER] ANSWERING CALL!!', call);

        call.on('stream', (remoteStream) => {
          console.log(
            'REMOTE STREAM ON CALL: ',
            remoteStream,
            remoteStream.getAudioTracks(),
            remoteStream.getVideoTracks()
          );

          if (
            remoteStream.getVideoTracks().length > 0 &&
            remoteStream.getAudioTracks().length > 0
          ) {
            console.log('AUDIO/VIDEO');
            setRemoteStream(remoteStream);
          } else if (
            remoteStream.getVideoTracks().length > 0 &&
            remoteStream.getAudioTracks().length === 0
          ) {
            console.log('SCREEN SHARE');
            setRemoteScreenStream(remoteStream);
          } else if (remoteStream.getAudioTracks().length > 0) {
            console.log('AUDIO ONLY');
            setRemoteStream(remoteStream);
          }
        });

        call.on('close', () => {
          console.log('CLOSING CALL!!');

          setRemoteStream(null);
          setRemoteScreenStream(null);
        });
      });

      peer.on('close', () => {
        console.log('CLOSING PEER!!');

        setRemoteStream(null);
        setRemoteScreenStream(null);
      });

      setPeerInstance(peer);
    }
  };

  // handler function to toggle user's video camera declared here
  const toggleUserCameraHandler = async () => {
    if (!localStream || !webSocket) return;

    const videoTrack = localStream
      .getTracks()
      .find((track) => track.kind === 'video');

    if (!videoTrack) return;

    videoTrack.enabled = !videoTrack.enabled;

    webSocket.send(
      JSON.stringify(
        currentUserType === 'user'
          ? {
              is_associate_video_muted: !videoTrack.enabled,
            }
          : {
              is_client_video_muted: !videoTrack.enabled,
            }
      )
    );

    setIsCameraEnabled(videoTrack.enabled);
  };

  // handler function to toggle user's MIC declared here
  const toggleUserMicHandler = async () => {
    if (!localStream || !webSocket) return;

    const audioTrack = localStream
      .getTracks()
      .find((track) => track.kind === 'audio');

    if (!audioTrack) return;

    audioTrack.enabled = !audioTrack.enabled;

    webSocket?.send(
      JSON.stringify(
        currentUserType === 'user'
          ? {
              is_associate_audio_muted: !audioTrack.enabled,
            }
          : {
              is_client_audio_muted: !audioTrack.enabled,
            }
      )
    );

    setIsMicEnabled(audioTrack.enabled);
  };

  // handler function to toggle screen share feature declared here
  const toggleScreenShareHandler = () => {
    if (!localScreenStream) {
      navigator.mediaDevices
        .getDisplayMedia({
          video: true,
          audio: false,
        })
        .then((stream) => {
          // adding eventlistener to capture "Stop Sharing" button click in the browser
          stream.getVideoTracks()[0].addEventListener('ended', () => {
            setLocalScreenStream(null);

            webSocket?.send(
              JSON.stringify(
                currentUserType === 'user'
                  ? {
                      is_associate_screen_shared: false,
                    }
                  : {
                      is_client_screen_shared: false,
                    }
              )
            );
          });

          setLocalScreenStream(stream);

          webSocket?.send(
            JSON.stringify(
              currentUserType === 'user'
                ? {
                    is_associate_screen_shared: true,
                  }
                : {
                    is_client_screen_shared: true,
                  }
            )
          );
        })
        .catch((error) =>
          toast.warn(error.message, { toastId: 'local-screen-stream' })
        );
    } else {
      localScreenStream.getTracks().forEach((track) => {
        track.stop();
        localScreenStream.removeTrack(track);
      });

      setLocalScreenStream(null);

      webSocket?.send(
        JSON.stringify(
          currentUserType === 'user'
            ? {
                is_associate_screen_shared: false,
              }
            : {
                is_client_screen_shared: false,
              }
        )
      );
    }
  };

  // handler function to handle call hangup feature
  const callHangUpHandler = () => {
    stopStreamTracksHandler(localStream, setLocalStream);
    stopStreamTracksHandler(localScreenStream, setLocalScreenStream);

    if (localScreenStreamRef.current) {
      localScreenStreamRef.current.srcObject = null;
    }

    if (peerInstance) {
      // playing user leaving audio sound here
      leavedSessionAudio.play();

      setShowFeedbackModal(true);

      peerInstance.disconnect();
      peerInstance.destroy();

      webSocket?.close(currentUserType === 'user' ? 1000 : 3000);
    }
  };

  console.log('LOCAL STREAM PROVIDER: ', localStream);

  useEffect(() => {
    return () => {
      callHangUpHandler();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  console.log('STATUS: ', mutedStatus);

  // rendering the context provider here
  return (
    <SessionContext.Provider
      value={{
        webSocket,
        socketData: socketData as SessionSocketData,
        peerInstance,
        cameraPermissions,
        micPermissions,
        videoDevices,
        setSelectedCamera,
        audioInputDevices,
        setSelectedMic,
        localStream,
        remoteStream,
        localScreenStream,
        remoteScreenStream,
        joinedClient,
        mutedStatus,
        isCameraLoading,
        isCameraEnabled,
        isMicEnabled,
        showFeedbackModal,
        setShowFeedbackModal,
        localStreamRef,
        remoteStreamRef,
        localScreenStreamRef,
        remoteScreenStreamRef,
        askCameraPermissionsHandler,
        askMicPermissionsHandler,
        initiateCallHandler,
        toggleUserCameraHandler,
        toggleUserMicHandler,
        toggleScreenShareHandler,
        callHangUpHandler,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export default SessionProvider;

// custom hook to access session context declared here
export const useSessionContext = () => {
  const context = useContext(SessionContext);

  if (!context) {
    throw new Error('useSessionContext must be within SessionProvider!');
  }

  return context;
};
